import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { AuthenticatedAdmin } from '@core/models/auth.model';
import { logoutThunk } from '@redux/features/login.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { changeLeftsidebarSizeType } from '@redux/slices/thunk';
import { RootState } from '@redux/store';
import logo from 'assets/images/logo.png';
import { Dropdown } from 'Common/Components/Dropdown';
import Avatar from 'components/ui/Avatar';
import { TOKEN_INFO } from 'core/constants/generic.constant';
import { AUTHPATHS } from 'core/enums/paths.enum';
import Cookies from 'js-cookie';
import { ChevronsLeft, ChevronsRight, LogOut, User2 } from 'lucide-react';
import { createSelector } from 'reselect';

const Header = (): ReactElement => {
    //-------------------------------------------------------------------------------------
    const admin = useAppSelector((state: RootState) => state.authState.admin);
    //-------------------------------------------------------------------------------------
    const dispatch = useDispatch<any>();
    const thunkDispatch = useAppDispatch();
    const navigate = useNavigate();
    //-------------------------------------------------------------------------------------
    const selectLayoutState = (state: any) => state.Layout;
    const selectLayoutProperties = createSelector(selectLayoutState, (layout: any) => ({
        layoutSidebarSizeType: layout.layoutSidebarSizeType,
        layoutType: layout.layoutType,
    }));

    const { layoutSidebarSizeType, layoutType } = useSelector(selectLayoutProperties);
    const logout = () => {
        navigate(AUTHPATHS.Login);
        Cookies.remove(TOKEN_INFO);
    };

    const handleTopbarHamburgerIcon = () => {
        const windowSize = document.documentElement.clientWidth;
        const sidebarOverlay = document.getElementById(`sidebar-overlay`) as any;

        if (windowSize < 768) {
            document.body.classList.add(`overflow-hidden`);
            if (sidebarOverlay.classList.contains(`hidden`)) {
                sidebarOverlay.classList.remove(`hidden`);
                (document as Document | any).querySelector(`.app-menu`).classList.remove(`hidden`);
            } else {
                sidebarOverlay.classList.add(`hidden`);
                (document as Document | any).querySelector(`.app-menu`).classList.add(`hidden`);
            }
            dispatch(changeLeftsidebarSizeType(`lg`));
        } else {
            dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === `sm` ? `lg` : `sm`));
        }
    };

    useEffect(() => {
        // resize
        const handleResizeLayout = () => {
            const windowSize = document.documentElement.clientWidth;

            if (windowSize < 768) {
                dispatch(changeLeftsidebarSizeType(`lg`));
            } else if (windowSize <= 1024) {
                if (layoutType === `vertical`) {
                    dispatch(changeLeftsidebarSizeType(`sm`));
                } else {
                    dispatch(changeLeftsidebarSizeType(`lg`));
                }
            } else {
                dispatch(changeLeftsidebarSizeType(`lg`));

                // dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === "sm" ? "lg" : "sm"));
            }
        };

        // out side click
        const outerSideElement = () => {
            const windowSize = document.documentElement.clientWidth;
            const sidebarOverlay = document.getElementById(`sidebar-overlay`) as any;
            if (sidebarOverlay) {
                sidebarOverlay.addEventListener(`click`, () => {
                    if (!sidebarOverlay.classList.contains(`hidden`)) {
                        if (windowSize <= 768) {
                            document?.querySelector(`.app-menu`)?.classList.add(`hidden`);
                            document.body.classList.remove(`overflow-hidden`);
                            sidebarOverlay.classList.add(`hidden`);
                        } else {
                            dispatch(changeLeftsidebarSizeType(`lg`));
                        }
                    }
                });
            }
        };

        // scroll
        const scrollNavigation = () => {
            const scrollUp = document.documentElement.scrollTop;
            if (scrollUp >= 50) {
                document.getElementById(`page-topbar`)?.classList.add(`is-sticky`);
            } else {
                document.getElementById(`page-topbar`)?.classList.remove(`is-sticky`);
            }
        };

        window.addEventListener(`scroll`, scrollNavigation, true);
        window.addEventListener(`click`, outerSideElement);
        window.addEventListener(`resize`, handleResizeLayout);

        // Cleanup function to remove the event listeners
        return () => {
            window.removeEventListener(`scroll`, scrollNavigation, true);
            window.removeEventListener(`click`, outerSideElement);
            window.removeEventListener(`resize`, handleResizeLayout);
        };
    }, [layoutType, dispatch]);
    return (
        <React.Fragment>
            <header
                id="page-topbar"
                className="group/topbar fixed left-0 right-0 z-[1000] transition-all duration-300 ease-linear group-data-[navbar=scroll]:absolute group-data-[layout=horizontal]:z-[1004] group-data-[navbar=bordered]:m-4 group-data-[navbar=hidden]:hidden group-data-[layout=horizontal]:ltr:left-0 ltr:md:left-vertical-menu group-data-[sidebar-size=md]:ltr:md:left-vertical-menu-md group-data-[sidebar-size=sm]:ltr:md:left-vertical-menu-sm group-data-[layout=horizontal]:rtl:right-0 rtl:md:right-vertical-menu group-data-[sidebar-size=md]:rtl:md:right-vertical-menu-md group-data-[sidebar-size=sm]:rtl:md:right-vertical-menu-sm print:hidden group-data-[navbar=bordered]:[&.is-sticky]:mt-0"
            >
                <div className="layout-width">
                    <div className="mx-auto flex h-header items-center border-b-2 border-topbar bg-topbar px-4 shadow-md shadow-slate-200/50 group-data-[navbar=bordered]:rounded-md group-data-[layout=horizontal]:group-data-[navbar=bordered]:rounded-b-none group-data-[topbar=brand]:border-topbar-brand group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=dark]:bg-topbar-dark group-data-[layout=horizontal]:shadow-none group-data-[navbar=bordered]:shadow-none group-data-[navbar=bordered]:group-[.is-sticky]/topbar:rounded-t-none dark:shadow-none group-data-[topbar=dark]:dark:border-zink-700 group-data-[topbar=dark]:dark:bg-zink-700 group-data-[layout=horizontal]:dark:group-[.is-sticky]/topbar:shadow-none group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-md group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-zink-500">
                        <div className="navbar-header flex w-full items-center group-data-[layout=horizontal]:mx-auto group-data-[layout=horizontal]:max-w-screen-2xl group-data-[layout=horizontal]:ltr:xl:pr-3 group-data-[layout=horizontal]:rtl:xl:pl-3">
                            <div className="group-data-[layout=horizontal]:ltr::pl-0 hidden h-header items-center justify-center px-5 text-center group-data-[layout=horizontal]:md:flex group-data-[layout=horizontal]:rtl:pr-0">
                                <Link to="/">
                                    <span className="hidden">
                                        <img src={logo} alt="" className="mx-auto h-6" />
                                    </span>
                                    <span className="group-data-[topbar=brand]:hidden group-data-[topbar=dark]:hidden">
                                        <img src={logo} alt="" className="mx-auto h-6" />
                                    </span>
                                </Link>
                                <Link
                                    to="/"
                                    className="hidden group-data-[topbar=brand]:block group-data-[topbar=dark]:block"
                                >
                                    <span className="group-data-[topbar=brand]:hidden group-data-[topbar=dark]:hidden">
                                        <img src={logo} alt="" className="mx-auto h-6" />
                                    </span>
                                    <span className="group-data-[topbar=brand]:block group-data-[topbar=dark]:block">
                                        <img src={logo} alt="" className="mx-auto h-6" />
                                    </span>
                                </Link>
                            </div>

                            <button
                                onClick={handleTopbarHamburgerIcon}
                                type="button"
                                className="hamburger-icon btn relative inline-flex size-[37.5px] items-center justify-center rounded-md bg-topbar p-0 text-topbar-item transition-all duration-75 ease-linear hover:bg-slate-100 group-data-[layout=horizontal]:flex group-data-[topbar=brand]:border-topbar-brand group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=dark]:dark:border-zink-700 group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[layout=horizontal]:md:hidden"
                                id="topnav-hamburger-icon"
                            >
                                <ChevronsLeft className="h-5 w-5 group-data-[sidebar-size=sm]:hidden" />
                                <ChevronsRight className="hidden h-5 w-5 group-data-[sidebar-size=sm]:block" />
                            </button>

                            <div className="ms-auto flex gap-3">
                                <Dropdown className="relative flex h-header items-center">
                                    <Dropdown.Trigger
                                        type="button"
                                        className="dropdown-toggle btn inline-block rounded-full bg-topbar p-0 text-topbar-item transition-all duration-200 ease-linear hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=dark]:dark:hover:text-zink-50"
                                        id="dropdownMenuButton"
                                        data-bs-toggle="dropdown"
                                    >
                                        <Avatar firsName={admin} />
                                    </Dropdown.Trigger>
                                    <Dropdown.Content
                                        placement="right-end"
                                        className="dropdown-menu absolute !top-4 z-50 min-w-[14rem] rounded-md bg-white p-4 shadow-md dark:bg-zink-600 ltr:text-left rtl:text-right"
                                        aria-labelledby="dropdownMenuButton"
                                    >
                                        <span className="mb-3 flex items-center gap-5">
                                            <div className="relative inline-block shrink-0">
                                                <Avatar firsName={admin} />
                                            </div>
                                            <div>
                                                <h6 className="text-15">{`${admin} ${admin}`}</h6>
                                                <p className="text-slate-500">{admin?.role?.department?.name}</p>
                                            </div>
                                        </span>
                                        <ul>
                                            {/* <li>
                                                <a
                                                    className="dropdown-item block py-1.5 text-base font-medium text-slate-600 transition-all duration-200 ease-linear hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500 ltr:pr-4 rtl:pl-4"
                                                    href={process.env.PUBLIC_URL + `/user-profile`}
                                                >
                                                    <User2 className="inline-block size-4 ltr:mr-2 rtl:ml-2"></User2>{' '}
                                                    Profile
                                                </a>
                                            </li> */}
                                            <li className="mt-2 border-t border-slate-200 pt-2 dark:border-zink-500">
                                                <button
                                                    className="dropdown-item block py-1.5 text-base font-medium text-slate-600 transition-all duration-200 ease-linear hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500 ltr:pr-4 rtl:pl-4"
                                                    onClick={logout}
                                                >
                                                    <LogOut className="inline-block size-4 ltr:mr-2 rtl:ml-2"></LogOut>
                                                    Sign Out
                                                </button>
                                            </li>
                                        </ul>
                                    </Dropdown.Content>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </React.Fragment>
    );
};

export default Header;
